import { omitUndefined } from '@shared/utils/omitUndefined';
import type { BusinessPlaneProvisioningStatus } from '@shared/tenant-host/businessPlaneProvisioningAccess';

export interface TenantInfo {
  /** tenants.tenant_id text slug — API x-tenant-id SSOT (not uuid PK) */
  id: string;
  name: string;
  displayName: string;
  status: 'active' | 'inactive' | 'suspended';
  provisioningStatus?: BusinessPlaneProvisioningStatus | null;
  intakeSkipped?: boolean;
  genesisCompletedAt?: string | null;
  createdAt: Date;
  settings: {
    theme: string;
    language: string;
    timezone: string;
    currency: string;
    fiscalYearStart: string;
    reportingStandard: 'K-IFRS' | 'IFRS' | 'GAAP';
    companyInfo?: {
      businessName: string;
      businessRegistration: string;
      address?: {
        street: string;
        city: string;
        country: string;
        postalCode: string;
      };
    };
  };
}

type TenantStatus = TenantInfo['status'];
type ReportingStandard = TenantInfo['settings']['reportingStandard'];

interface TenantSettingsRow {
  theme?: string;
  language?: string;
  timezone?: string;
  currency?: string;
  fiscalYearStart?: string;
  fiscal_year_start?: string;
  reportingStandard?: ReportingStandard;
  reporting_standard?: ReportingStandard;
  companyInfo?: TenantInfo['settings']['companyInfo'];
  company_info?: TenantInfo['settings']['companyInfo'];
}

export interface TenantRow {
  id: string;
  tenant_id?: string | null;
  name?: string | null;
  display_name?: string | null;
  displayName?: string | null;
  status?: string | null;
  provisioning_status?: string | null;
  created_at?: string | null;
  createdAt?: string | null;
  settings?: TenantSettingsRow | null;
}

const PROVISIONING_STATUSES = new Set<BusinessPlaneProvisioningStatus>([
  'draft',
  'provisioning',
  'provisioned',
  'intake_pending',
  'ready',
  'failed',
]);

function parseProvisioningStatus(value: unknown): BusinessPlaneProvisioningStatus | null {
  if (typeof value !== 'string') return null;
  return PROVISIONING_STATUSES.has(value as BusinessPlaneProvisioningStatus)
    ? (value as BusinessPlaneProvisioningStatus)
    : null;
}

function parseTenantProvisioningFields(settings: TenantSettingsRow | null | undefined): {
  intakeSkipped?: boolean;
  genesisCompletedAt?: string | null;
} {
  const raw = settings as Record<string, unknown> | null | undefined;
  if (!raw) return {};

  const site = (raw.site as Record<string, unknown> | undefined) ?? {};
  const genesisCompletedAt =
    typeof site.genesisCompletedAt === 'string' ? site.genesisCompletedAt : null;

  return omitUndefined({
    ...(raw.intakeSkipped === true ? { intakeSkipped: true } : {}),
    ...(genesisCompletedAt ? { genesisCompletedAt } : {}),
  });
}

const DEFAULT_TENANT_SETTINGS: TenantInfo['settings'] = {
  theme: 'light',
  language: 'ko',
  timezone: 'Asia/Seoul',
  currency: 'KRW',
  fiscalYearStart: '01-01',
  reportingStandard: 'K-IFRS',
};

function parseTenantStatus(status: string | null | undefined): TenantStatus {
  if (status === 'inactive' || status === 'suspended') {
    return status;
  }
  return 'active';
}

function mapSettings(raw: TenantSettingsRow | null | undefined): TenantInfo['settings'] {
  if (!raw) {
    return { ...DEFAULT_TENANT_SETTINGS };
  }

  const reportingRaw = raw.reportingStandard ?? raw.reporting_standard;
  const reportingStandard: ReportingStandard =
    reportingRaw === 'IFRS' || reportingRaw === 'GAAP' ? reportingRaw : 'K-IFRS';

  return omitUndefined({
    theme: raw.theme ?? DEFAULT_TENANT_SETTINGS.theme,
    language: raw.language ?? DEFAULT_TENANT_SETTINGS.language,
    timezone: raw.timezone ?? DEFAULT_TENANT_SETTINGS.timezone,
    currency: raw.currency ?? DEFAULT_TENANT_SETTINGS.currency,
    fiscalYearStart:
      raw.fiscalYearStart ?? raw.fiscal_year_start ?? DEFAULT_TENANT_SETTINGS.fiscalYearStart,
    reportingStandard,
    companyInfo: raw.companyInfo ?? raw.company_info,
  });
}

/** API·RLS SSOT — tenants.tenant_id (text slug). UUID PK(id)는 사용하지 않는다. */
export function resolveTenantSlugFromRow(row: TenantRow): string {
  const slug = row.tenant_id?.trim();
  if (slug) {
    return slug;
  }
  return row.id;
}

export function mapRowToTenantInfo(row: TenantRow): TenantInfo {
  const tenantSlug = resolveTenantSlugFromRow(row);
  const createdRaw = row.created_at ?? row.createdAt;
  const createdAt = createdRaw ? new Date(createdRaw) : new Date();

  const provisioningFields = parseTenantProvisioningFields(row.settings);

  return {
    id: tenantSlug,
    name: row.name ?? `Tenant ${tenantSlug}`,
    displayName: row.display_name ?? row.displayName ?? `테넌트 ${tenantSlug}`,
    status: parseTenantStatus(row.status),
    provisioningStatus: parseProvisioningStatus(row.provisioning_status),
    ...provisioningFields,
    createdAt: Number.isNaN(createdAt.getTime()) ? new Date() : createdAt,
    settings: mapSettings(row.settings),
  };
}
